import { buildTemplateSelectItems, getSmartDefaultTemplate } from "@utils/helpers";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useMemo } from "react";

import { Select, SelectItem } from "@fronterahealth/frontera-ui-components";

import {
  ReportTemplateStatus,
  ReportTemplateTypeEdge,
  ReportTypeEnums,
  useReportTemplatesQuery,
} from "@api/graphql/types-and-hooks";
import { useAdminData } from "@providers/AdminDataProvider";

interface SelectTemplateProps {
  reportType: ReportTypeEnums;
  preselectTemplate: boolean;
  selectedTemplate: SelectItem | null;
  setSelectedTemplate: (val: SelectItem | null) => void;
  /**
   * Selected client. When set, the API narrows templates to the ones matching
   * that client's payor, falling back to every org template when the payor has
   * none of its own.
   */
  learnerId?: string | null;
}

export const SelectTemplate: React.FC<SelectTemplateProps> = ({
  reportType,
  preselectTemplate,
  selectedTemplate,
  setSelectedTemplate,
  learnerId,
}) => {
  const flags = useFlags();
  const isDemoOrgMode = !!flags["demo-org-mode"];
  const { isUserEnterpriseAdmin } = useAdminData();

  const statuses = isUserEnterpriseAdmin
    ? [ReportTemplateStatus.Published, ReportTemplateStatus.Draft]
    : [ReportTemplateStatus.Published];

  const { data: templatesData, isLoading: templatesLoading } = useReportTemplatesQuery(
    {
      filter: {
        reportType: reportType,
        statuses,
        learner: learnerId ?? undefined,
      },
    },
    {
      queryKey: ["get-published-report-templates-by-type", reportType, isUserEnterpriseAdmin, learnerId],
    },
  );

  const templateEdges = templatesData?.getReportTemplates?.edges;

  const templateSelections = useMemo(
    () => buildTemplateSelectItems((templateEdges ?? []) as ReportTemplateTypeEdge[], isDemoOrgMode),
    [templateEdges, isDemoOrgMode],
  );

  useEffect(() => {
    if (preselectTemplate && templateSelections.length > 0 && !selectedTemplate) {
      const smartDefault = getSmartDefaultTemplate(templateSelections);
      if (smartDefault) {
        setSelectedTemplate(smartDefault);
      }
    }
  }, [preselectTemplate, templateSelections, selectedTemplate]);

  return (
    <Select
      title="Select Template"
      isLoading={templatesLoading}
      items={templateSelections}
      placeholderText={
        templateSelections.length > 1 && !selectedTemplate ? "Select a template..." : "Type to find a template"
      }
      selected={selectedTemplate}
      setSelected={setSelectedTemplate}
      enableSearch
    />
  );
};
